package com.techspiration.core.contract

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// ─── Ortak ───────────────────────────────────────────────────────────────────

@Serializable
data class HealthResponse(val status: String, val service: String)

@Serializable
data class ApiError(
    val code: String,
    val message: String,
    /** Hatanın ilişkili olduğu alan (örn. "email") — inline/form gösterimi için, opsiyonel. */
    val field: String? = null,
)

@Serializable
data class ErrorResponse(val error: ApiError)

/**
 * Backend business-logic hata kodları — **backend + mobil ortak, type-safe kaynak**.
 *
 * [ApiError.code] wire üzerinde String taşınır (geri uyumlu + serileştirme güvenli); iki uç da
 * [wire] ↔ [fromWire] ile [ErrorCode]'a çevirir. Bilinmeyen kod → [UNKNOWN] (forward-compat:
 * eski client yeni kodu, yeni client eski kodu güvenle görmezden gelir — serileştirme patlamaz).
 *
 * Yeni bir business hatası eklerken: buraya bir değer ekle, backend `respondError(ErrorCode.X)`,
 * mobil `when (errorCode)` ile handle eder.
 */
enum class ErrorCode(val wire: String) {
    VALIDATION("validation"),
    AUTH("auth"),
    FORBIDDEN("forbidden"), // cihaz bütünlüğü doğrulanamadı (compromised) → 403
    CONFLICT("conflict"),
    NOT_FOUND("notFound"),
    INVALID_TOKEN("invalidToken"),
    INVALID_REFRESH("invalidRefresh"),
    GOOGLE_UNVERIFIED("googleUnverified"),
    STORE_UNAVAILABLE("storeUnavailable"),
    VERIFIER_MISCONFIGURED("verifierMisconfigured"),
    INTERNAL("internal"),
    UNKNOWN("unknown"),
    ;

    companion object {
        /** Wire (backend) string kodunu [ErrorCode]'a çevirir; bilinmeyen kod → [UNKNOWN]. */
        fun fromWire(wire: String): ErrorCode = entries.firstOrNull { it.wire == wire } ?: UNKNOWN
    }
}

/**
 * HTTP hatası taşıyıcısı — sunucunun yapısal [ApiError]'ı (parse edilebildiyse) + HTTP [httpStatus].
 *
 * Mobil katmanlar arası TİPLİ hata köprüsü: data katmanı `Result.failure(ApiException(...))` döner,
 * UI katmanı `AppError.from(...)` ile [apiError]?.code ([ErrorCode.fromWire]) + [httpStatus]'a göre
 * ayrıştırır. [apiError] null ise (gövde yok/parse edilemedi) yalnızca [httpStatus] anlamlıdır.
 *
 * core:contract Ktor bilmez; orijinal exception [cause]'da genel [Throwable] olarak taşınır.
 */
class ApiException(
    val httpStatus: Int,
    val apiError: ApiError?,
    override val cause: Throwable? = null,
) : Exception(apiError?.message ?: "HTTP $httpStatus", cause) {

    /** Yapısal business hata kodu (varsa); yoksa [ErrorCode.UNKNOWN]. */
    val errorCode: ErrorCode get() = apiError?.let { ErrorCode.fromWire(it.code) } ?: ErrorCode.UNKNOWN
}

// ─── Auth ──────────────────────────────────────────────────────────────────

@Serializable
data class RegisterRequest(
    val email: String,
    val password: String,
    val displayName: String,
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
)

@Serializable
data class UserDto(
    val id: Long,
    val email: String,
    val displayName: String,
    val photoUrl: String? = null, // Google avatarı; email auth'ta null (geriye uyumlu)
)

@Serializable
data class AuthResponse(
    val token: String,
    val refreshToken: String, // uzun ömürlü rotation token; access token süresi dolunca /auth/refresh ile kullanılır
    val user: UserDto,
    val isNewUser: Boolean = false, // /auth/google → register (true) / login (false); email auth'ta default false
)

@Serializable
data class GoogleAuthRequest(
    val idToken: String,
    val device: DeviceInfoDto? = null, // register/login anındaki cihaz + analitik snapshot (opsiyonel, geriye uyumlu)
)

/**
 * Sign in with Apple isteği (iOS native).
 *
 * [idToken] Apple'ın döndürdüğü identity token (JWT). Backend bunu Apple public key'leri ile doğrular.
 * [fullName] Apple kullanıcı adını YALNIZCA ilk yetkilendirmede verir; identity token'da `name` claim'i
 * bulunmaz → sonraki girişlerde null gelir, bu yüzden ayrı taşınır (yeni kullanıcı displayName'i için).
 * [device] register/login anındaki cihaz + analitik snapshot (opsiyonel, geriye uyumlu).
 */
@Serializable
data class AppleAuthRequest(
    val idToken: String,
    val fullName: String? = null,
    /**
     * Apple `authorizationCode` (her yetkilendirmede gelir; kısa ömürlü, tek kullanımlık). Backend bunu
     * Apple token-endpoint'inde refresh_token'a çevirip saklar → hesap silmede Apple token revoke için.
     * Yoksa/credential yoksa revoke adımı atlanır (best-effort). Geriye uyumlu (opsiyonel).
     */
    val authorizationCode: String? = null,
    val device: DeviceInfoDto? = null,
)

/**
 * Register/login anında toplanan cihaz + analitik bilgisi. Tüm alanlar opsiyonel (geriye uyumlu).
 * IP / coğrafi konum / userAgent backend'de çıkarılır; burada yalnızca client-side toplanabilenler taşınır.
 * Kimlik: kullanıcı-uniq backend'deki googleId; cihaz-uniq [installId] (izinsiz, KVKK-güvenli) + [fcmToken].
 */
@Serializable
data class DeviceInfoDto(
    val platform: String? = null, // "android" | "ios"
    val osVersion: String? = null,
    val deviceModel: String? = null,
    val manufacturer: String? = null,
    val appVersion: String? = null,
    val buildNumber: String? = null,
    val locale: String? = null,
    val timeZone: String? = null,
    val screenWidth: Int? = null,
    val screenHeight: Int? = null,
    val screenDensity: Float? = null,
    val connectionType: String? = null, // wifi | cellular | ...
    val fcmToken: String? = null,
    val installId: String? = null, // kuruluma özel UUID (DataStore'da kalıcı)
)

/** Süresi dolan access token'ı yenilemek için gönderilen refresh token. */
@Serializable
data class RefreshRequest(val refreshToken: String)

/** Yeni access token + rotasyonla üretilen yeni refresh token. */
@Serializable
data class RefreshResponse(
    val token: String,
    val refreshToken: String,
)

/**
 * `POST /api/v1/support/messages` gövdesi — kullanıcının yazdığı metin.
 *
 * `senderId`/`senderType`/`timestamp` BİLEREK YOK: bunları sunucu JWT'den ve kendi saatinden
 * yazar; istemciden gelmeleri kimlik/zaman sahtelemesine açık kapı bırakırdı.
 */
@Serializable
data class SupportMessageSendRequest(val text: String)

// ─── Profil (onboarding tamamlama) ────────────────────────────────────────────

/** Seçili meslek referansı: stabil anahtar + görünen ad. */
@Serializable
data class ProfessionRef(val key: String, val name: String)

@Serializable
data class MykDto(val field: MykField, val level: MykLevel)

/** Belge & sertifikalar — `profiles.documents` JSONB olarak saklanır. */
@Serializable
data class DocumentsDto(
    val driverLicenses: List<DriverLicenseClass> = emptyList(),
    val srcClasses: List<SrcClass> = emptyList(),
    val hasPsychotechnical: Boolean = false,
    val securityCard: SecurityCardType = SecurityCardType.none,
    val hasComputerCertificate: Boolean = false,
    val hasFirstAid: Boolean = false,
    val hasPedagogicalFormation: Boolean = false,
    /** Katalogdan seçilen serbest sertifika/belge adları (jsonb içinde saklanır). */
    val certificates: List<String> = emptyList(),
)

/** PUT /profile gövdesi — tüm alanlar opsiyonel (kademeli doldurma). */
@Serializable
data class ProfileRequest(
    val nickname: String? = null,
    val age: Int? = null,
    val heightCm: Int? = null,
    val weightKg: Int? = null,
    val gender: Gender? = null,
    val city: String? = null,
    val educationLevel: EducationLevel? = null,
    val isStudent: Boolean = false,
    val hasDisability: Boolean = false,
    val kpssP3: Double? = null,
    val kpssP93: Double? = null,
    val kpssP94: Double? = null,
    val yds: Double? = null,
    val ales: Double? = null,
    val professions: List<ProfessionRef> = emptyList(),
    val documents: DocumentsDto = DocumentsDto(),
)

@Serializable
data class ProfileDto(
    val userId: Long,
    val nickname: String? = null,
    val age: Int? = null,
    val heightCm: Int? = null,
    val weightKg: Int? = null,
    val gender: Gender? = null,
    val city: String? = null,
    val educationLevel: EducationLevel? = null,
    val isStudent: Boolean = false,
    val hasDisability: Boolean = false,
    val kpssP3: Double? = null,
    val kpssP93: Double? = null,
    val kpssP94: Double? = null,
    val yds: Double? = null,
    val ales: Double? = null,
    val professions: List<ProfessionRef> = emptyList(),
    val documents: DocumentsDto = DocumentsDto(),
    val completionScore: Int = 0,
    val updatedAt: Long = 0L,
)

@Serializable
data class ProfileResponse(val profile: ProfileDto, val completionScore: Int)

// ─── Sınav geri sayımı (KPSS / ALES / YDS) ─────────────────────────────────────

/**
 * Tek bir sınav oturumuna kalan süre.
 *
 * @param key benzersiz oturum anahtarı (örn. `kpss_lisans`, `ales_2`, `yds_2`)
 * @param name görünen ad (örn. "KPSS Lisans", "ALES/2")
 * @param examTimestamp sınav anının epoch millis değeri (UTC)
 * @param remainingMillis `examTimestamp - now`; tarihi geçen oturumda **0** (negatif olmaz)
 */
@Serializable
data class ExamCountdownDto(
    val key: String,
    val name: String,
    val examTimestamp: Long,
    val remainingMillis: Long,
)

@Serializable
data class ExamCountdownResponse(val exams: List<ExamCountdownDto>)

// ─── Sınav tarihleri (admin yönetimi) ─────────────────────────────────────────

/** Admin okuma: tek sınav tarihi kaydı (examDate = "yyyy-MM-dd", Türkiye saati). */
@Serializable
data class ExamDateItem(
    val key: String,
    val name: String,
    val examTimestamp: Long,
    val examDate: String,
)

/** GET /admin/web/exam-dates yanıtı. */
@Serializable
data class ExamDatesResponse(
    val exams: List<ExamDateItem>,
)

/** Admin yazma: bir sınavın güncellenecek alanları (examDate = "yyyy-MM-dd"). */
@Serializable
data class ExamDateUpdateItem(
    val key: String,
    val name: String,
    val examDate: String,
)

/** PUT /admin/web/exam-dates gövdesi — toplu upsert. */
@Serializable
data class ExamDatesUpdateRequest(
    val exams: List<ExamDateUpdateItem>,
)

// ─── İlan listesi (dış kaynaklardan derlenen) ─────────────────────────────────

/**
 * İlanın istihdam biçimi — hangi kadroya/ilişkiye alım yapıldığı.
 *
 * Kaynak ayrımı değildir ([ListingItem.source] onu taşır): aynı kaynak (İŞKUR) her iki tipte
 * ilan yayımlar. Tip, kullanıcının başvuru koşullarını belirlediği için ayrı bir alandır.
 */
@Serializable
enum class ListingType {
    /**
     * Kurumun KENDİ kadrosuna alımı (memur, sözleşmeli personel). Bugüne kadar toplanan
     * TÜM ilanlar bu tiptedir — bu yüzden varsayılan budur ve eski istemciler/kayıtlar
     * alan gelmediğinde doğru değeri görür.
     */
    KURUM_ICI,

    /**
     * İŞKUR "kurum dışı kamu işçi alımı": kamu kurumuna hizmet veren şirket/taşeron
     * üzerinden işçi alımı. Başvuru İŞKUR üzerinden yürür, kadro kurumun değildir.
     */
    KURUM_DISI,
}

/**
 * Tek bir ilan — kaynak metninden çıkarılan tüm kriterler düz (flat) alanlar olarak.
 *
 * Skorlar `Map<String, Double>` (örn. anahtar `"KPSSP3"`), eğitim/meslek/branş listeleri ise
 * serbest `String` listesidir. `ageRequirement` ve `driverLicenseRequirement` veri kaynağında
 * (`kariyerkapisi`) yapısız serbest metin olarak gelir (şu an `String?`; ileride yapılandırılmış
 * parse eklenirse tipler yeniden ele alınır).
 *
 * @param source ilanın geldiği kaynak (örn. `"kariyerkapisi"`)
 * @param uniqValue kaynaktaki benzersiz ilan kimliği (yoksa `null`)
 * @param institutionName ilanı veren kurum/kuruluş adı (örn. `"Sağlık Bakanlığı"`); kaynakta yoksa `null`
 * @param cities ilanın geçerli olduğu il listesi
 * @param district aynı il içinde ilanı ayırt eden ilçe/adliye (örn `"Bakırköy"`); il merkezi / çok-il / Türkiye geneli ilanlarda `null`
 * @param quotaCount toplam kontenjan (kaynakta belirsizse `null`)
 * @param type istihdam biçimi (kurum içi kadro / kurum dışı işçi alımı); bkz. [ListingType]
 */
@Serializable
data class ListingItem(
    val source: String,
    val uniqValue: String? = null,
    val institutionName: String? = null,
    val cities: List<String> = emptyList(),
    val district: String? = null,
    val quotaCount: Int? = null,
    /** Başvuru başlangıç tarih-saati, ISO 8601 string (örn. `"2026-06-02T08:00:00"`); yoksa `null`. */
    val applicationStartDate: String? = null,
    /** Başvuru bitiş tarih-saati, ISO 8601 string (örn. `"2026-06-04T17:00:00"`); yoksa `null`. */
    val applicationEndDate: String? = null,
    val occupations: List<String> = emptyList(),
    val educationLevels: List<String> = emptyList(),
    /**
     * AI, ilan metnini (varsa PDF/OCR görseli dahil) dikkatlice tarayıp HİÇBİR eğitim/öğrenim
     * şartı bulamadığını teyit ederse `true` (bkz. `ListingsPrompt` "farketmez" sentinel'i).
     * `educationLevels` boşken bu alan `false` ise "AI hiçbir çıkarım yapamadı" (eski davranış,
     * bilinmiyor) anlamına gelir — `true` ise "AI baktı, gerçekten şart yok" (kesin bilgi).
     */
    val educationLevelUnspecified: Boolean = false,
    val kpssScores: Map<String, Double>? = null,
    val alesScores: Map<String, Double>? = null,
    val ydsScore: Double? = null,
    val bachelorFields: List<String> = emptyList(),
    val associateFields: List<String> = emptyList(),
    val ageRequirement: String? = null,
    val genderRequirement: String? = null,
    val driverLicenseRequirement: String? = null,
    /** İlanda engelli kontenjanı/alımı olduğu belirtilmiş mi (AI çıkarımı, ListingsPrompt). */
    val hasDisabilityQuota: Boolean = false,
    val requiredDocuments: List<String> = emptyList(),
    val requiredCertificates: List<String> = emptyList(),
    /** Başvuru / ilan sayfası URL'si (kaynak: `applyUrl`); yoksa `null`. */
    val applyUrl: String? = null,
    /**
     * İstihdam biçimi. Varsayılan [ListingType.KURUM_ICI] — alan tel üzerinde gelmezse
     * (eski toplayıcı / eski kayıt) doğru değer budur, bu yüzden geriye uyumludur.
     */
    val type: ListingType = ListingType.KURUM_ICI,
    /**
     * **YALNIZCA cliApp → server yönünde doludur; mobil yanıtlarda HER ZAMAN `null`'dır.**
     *
     * İlanın cliApp tarafından toplandığı an (epoch ms, UTC). Bir toplama döngüsündeki TÜM
     * ilanlar aynı değeri taşır (döngü başlangıcı) — "bu ilan hangi turda geldi" sorusunun
     * cevabıdır. Server bunu `listings.collectedAt` kolonuna yazar; okuma tarafı
     * (`ListingsByEducationRepository.assembleMap`) bu alanı BİLEREK doldurmaz, böylece
     * mobil `GET /listings` yanıtı değişmez ve alan `explicitNulls=false` ile telden düşer.
     * Admin görünürlüğü ayrı bir yüzeyden gelir: `ADMIN_WEB_PIPELINE_INGESTS/{id}/listings`.
     */
    val collectedAt: Long? = null,
)

/**
 * İlan listesi yanıtı.
 *
 * @param listings döndürülen ilanlar (login: profil-filtreli tam liste / misafir: yaklaşan 5 aktif ilan)
 * @param isGuest token yokken `true` → mobil "kayıt ol / giriş yap" CTA'sını buna göre gösterir (geriye uyumlu, default `false`)
 */
@Serializable
data class ListingsResponse(
    val listings: List<ListingItem>,
    val isGuest: Boolean = false,
)

/** Admin web kullanıcı listesi — tek kullanıcının özet bilgisi. */
@Serializable
data class UserSummaryItem(
    val id: Long,
    val email: String,
    val createdAt: Long,
    /**
     * Kullanıcının uygulamaya ilk kayıt olduğu mağaza/platform.
     * Beklenen değerler: `GOOGLE_PLAY`, `APP_STORE`; eski kayıtlarda `null`.
     */
    val registrationStore: String? = null,
    /** Play Store'da (Android) aktif premium aboneliği var mı (geriye uyumlu, default `false`). */
    val isPlaySubscriber: Boolean = false,
    /** App Store'da (iOS) aktif premium aboneliği var mı (geriye uyumlu, default `false`). */
    val isAppStoreSubscriber: Boolean = false,
    /**
     * **Admin premium**: mağaza satın alması olmadan verilmiş statik premium erişimi
     * (`users.adminPremium`). [isPlaySubscriber]/[isAppStoreSubscriber]'dan BAĞIMSIZDIR ve gelir
     * raporlarına girmez; admin panelde ayrı gösterilir (geriye uyumlu, default `false`).
     */
    val isAdminPremium: Boolean = false,
    /**
     * [isPlaySubscriber] aboneliğinin yenileme periyodu (aylık/yıllık). Yalnızca mağazanın AÇIK
     * ayrımından türetilir; belirsiz/tanımsız planda `null` kalır ("bilinmiyor" olarak gösterilir,
     * aylık VARSAYILMAZ). Abone olmayan kullanıcıda ve admin premium'da daima `null`; alan
     * nullable + default `null` olduğu için eski istemciler etkilenmez.
     */
    val playSubscriptionPeriod: SubscriptionPeriod? = null,
    /** [isAppStoreSubscriber] aboneliğinin yenileme periyodu — kuralları [playSubscriptionPeriod] ile aynı. */
    val appStoreSubscriptionPeriod: SubscriptionPeriod? = null,
)

/** GET /admin/web/users yanıtı. */
@Serializable
data class UsersResponse(
    val users: List<UserSummaryItem>,
)

// ─── Admin abonelik görünürlüğü ───────────────────────────────────────────────

/**
 * Tek mağazanın abonelik görünürlüğü.
 *
 * [linkedActive], uygulamanın purchase verify akışıyla bir KamuApp kullanıcısına bağlayabildiği
 * aktif kayıttır. [storeActive] ve diğer nullable alanlar ise doğrudan mağaza raporu/API
 * senkronundan gelir; bağlantı kurulmadıysa null kalır ve sıfır gibi yorumlanmamalıdır.
 *
 * [sourceStatus] beklenen değerleri: `CONNECTED`, `PENDING`, `ERROR`.
 */
@Serializable
data class StoreSubscriptionMetricsDto(
    val platform: String,
    val linkedActive: Int,
    /** Mağaza raporunun seçili/son dönemindeki yeni abonelik başlangıçları (kümülatif). */
    val periodNew: Int? = null,
    /** Mağaza raporunun seçili/son dönemindeki iptaller (erişim hemen bitmiş demek değildir). */
    val periodCanceled: Int? = null,
    /**
     * Mağaza raporunun "Active Subscriptions" metriği. Otomatik yenilemesi kapatılmış fakat
     * ödenmiş dönemi henüz bitmemiş abonelikler bu sayıdan farklı bir erişim durumuna sahip olabilir.
     */
    val storeActive: Int? = null,
    val trial: Int? = null,
    val grace: Int? = null,
    val retry: Int? = null,
    val refunded: Int? = null,
    val lastSyncedAt: Long? = null,
    val sourceStatus: String = "PENDING",
    val message: String? = null,
)

/** `GET /api/admin/subscriptions/overview` yanıtı. */
@Serializable
data class AdminSubscriptionOverviewDto(
    val google: StoreSubscriptionMetricsDto,
    val apple: StoreSubscriptionMetricsDto,
)

// ─── Finans (abonelik geliri özeti) ───────────────────────────────────────────

/**
 * GET /admin/web/finance yanıtı.
 *
 * `platforms[].grossMicros/netMicros/...` ve `monthly[]` abonelik gelirinin TAHMİNİ TL özetidir
 * (sabit fiyat kataloğundan hesaplanır — muhasebe source-of-truth DEĞİLDİR). Buna karşılık
 * `adRevenueAvailable`/`totalAdRevenueMicros`/`platforms[].adRevenueMicros` Google AdMob Reporting
 * API'sinden çekilen GERÇEK reklam kazancıdır — TAHMİNİ DEĞİLDİR, ayrı bir veri kaynağıdır; bu
 * ikisi KARIŞTIRILMAMALIDIR. AdMob OAuth refresh-token env'leri (bkz. `ADMOB_OAUTH_CLIENT_ID` /
 * `ADMOB_OAUTH_CLIENT_SECRET` / `ADMOB_OAUTH_REFRESH_TOKEN`) tanımlı değilse
 * `adRevenueAvailable=false` döner ve ilgili alanlar null kalır.
 */
@Serializable
data class FinanceResponse(
    val currency: String,
    val estimated: Boolean = true,
    val rates: FinanceRatesDto,
    val platforms: List<PlatformFinanceDto>,
    val totalGrossMicros: Long,
    val totalCommissionMicros: Long,
    val totalTaxMicros: Long,
    val totalNetMicros: Long,
    val totalSubscriberCount: Int,
    val totalRefundCount: Int,
    val monthly: List<MonthlyFinanceDto>,
    val adSpendMicros: Long? = null,
    /** Google AdMob Reporting API'den GERÇEK veri çekilebildi mi (service account hesaba eklenmiş mi). false ise aşağıdaki adRevenue* alanları null'dur. */
    val adRevenueAvailable: Boolean = false,
    /** [adRevenueAvailable] true ise TL'ye çevrilmiş toplam GERÇEK AdMob kazancı (platforms[].adRevenueMicros toplamı). TAHMİNİ abonelik gelirinden AYRI bir kaynaktır. */
    val totalAdRevenueMicros: Long? = null,
    /** AdMob'un döndürdüğü orijinal para birimi (örn. "USD"); yukarıdaki tutarlar bu koddan TL'ye çevrilmiştir. */
    val adRevenueSourceCurrency: String? = null,
)

/** Ekranda görünür kılınan oranlar (komisyon/vergi/kur notu). */
@Serializable
data class FinanceRatesDto(
    val playCommissionPercent: Double,
    val appStoreFirstYearPercent: Double,
    val appStoreAfterOneYearPercent: Double,
    val withholdingTaxPercent: Double,
    val fxNote: String,
)

/** Tek platformun (Google Play / App Store) finans özeti. */
@Serializable
data class PlatformFinanceDto(
    val platform: String,
    val label: String,
    val subscriberCount: Int,
    val grossMicros: Long,
    val commissionMicros: Long,
    val taxMicros: Long,
    val netMicros: Long,
    val refundCount: Int,
    /** GERÇEK AdMob reklam kazancı (TL micros, bu platform için). null ise AdMob verisi mevcut değil — bkz. [FinanceResponse.adRevenueAvailable]. */
    val adRevenueMicros: Long? = null,
)

/** Aydan aya (tahmini) gelir satırı. */
@Serializable
data class MonthlyFinanceDto(
    val yearMonth: String,
    val grossMicros: Long,
    val netMicros: Long,
    val subscriberCount: Int,
    val refundCount: Int,
)

// ─── Başvurular (applied listings) ────────────────────────────────────────────

/** POST /applications gövdesi — başvurulan ilanın tamamı (snapshot kaynağı). */
@Serializable
data class ApplyRequest(val listing: ListingItem)

/** Tek başvuru kaydı — snapshot başvuru anındaki ilandır. */
@Serializable
data class ApplicationDto(
    val listingKey: String,
    val listing: ListingItem,
    val appliedAt: Long,
)

@Serializable
data class ApplicationsResponse(val applications: List<ApplicationDto>)

// ─── Favoriler (favorited listings) ───────────────────────────────────────────

/** POST /favorites gövdesi — favorilenen ilanın tamamı (server applicationKey() ile key üretir). */
@Serializable
data class FavoriteRequest(val listing: ListingItem)

/** GET /favorites yanıtı — favorilenen ilanların listingKey listesi (snapshot tutulmaz). */
@Serializable
data class FavoritesResponse(val favorites: List<String>)

// ─── İlan hatırlatmaları (listing reminders, PREMIUM-ONLY özellik) ────────────

/**
 * POST /listings/reminders body. [applicationEndDate], [ListingItem.applicationEndDate] ile AYNI
 * ISO 8601 string formatı — backend bu tarihe göre hatırlatmanın ne zaman "due" olduğuna karar verir.
 */
@Serializable
data class ListingReminderRequest(val listingKey: String, val applicationEndDate: String)

/** GET /listings/reminders yanıtı — hatırlatma kurulan ilanların listingKey listesi. */
@Serializable
data class ListingReminderKeysResponse(val reminderKeys: List<String>)

// ─── Dosya yükleme ────────────────────────────────────────────────────────────

/** Yüklenen dosyanın server tarafında ürettiği kimlik (UUID + uzantı, örn. `…abc.pdf`). */
@Serializable
data class UploadResponse(val fileId: String)

// ─── E2E Encryption (hibrit RSA-OAEP + AES-256-GCM) ────────────────────────────

/**
 * Sunucunun RSA public key'i — client handshake (`GET /auth/init`) ile alır ve payload AES
 * anahtarını bununla (RSA-OAEP) şifreler.
 *
 * @property publicKey Base64-encoded X.509 SubjectPublicKeyInfo (SPKI) DER.
 * @property keyId Anahtar rotasyonu için opsiyonel kimlik (sunucu birden çok anahtar tutarsa).
 */
@Serializable
data class PublicKeyResponse(
    val publicKey: String,
    val keyId: String? = null,
)

/**
 * Şifreli payload zarfı (hibrit). Asıl JSON gövdesi rastgele bir AES-256 anahtarıyla AES-GCM ile
 * şifrelenir; AES anahtarı sunucunun RSA public key'i ile (RSA-OAEP, SHA-256) şifrelenir.
 *
 * İstemci → sunucu (request body) ve sunucu → istemci (response body) aynı zarfı kullanır.
 *
 * @property encryptedKey RSA-OAEP ile şifreli AES-256 anahtarı (Base64).
 * @property iv AES-GCM nonce/IV (Base64, 12 byte).
 * @property ciphertext AES-GCM şifreli gövde + 128-bit auth tag (Base64).
 * @property keyId Hangi sunucu anahtarının kullanıldığı (rotasyon; opsiyonel).
 */
@Serializable
data class EncryptedEnvelope(
    val encryptedKey: String,
    val iv: String,
    val ciphertext: String,
    val keyId: String? = null,
)

/**
 * E2E şifreleme demo'su — istek gövdesi (AES-GCM ile şifrelenmeden ÖNCEki düz JSON).
 *
 * `POST /api/v1/secure/echo`: client bu DTO'yu JSON'a serialize eder, [EncryptedEnvelope] içinde
 * şifreli gönderir; sunucu çözüp [SecureEchoResponse] üretir ve AYNI oturum AES anahtarıyla
 * şifreleyip geri döner. Gerçek bir uçtan uca turu kanıtlar.
 */
@Serializable
data class SecureEchoRequest(
    val message: String,
)

/**
 * E2E şifreleme demo'su — sunucu yanıtı (çözüldükten SONRAki düz JSON).
 *
 * @property echo Sunucunun çözüp geri yansıttığı mesaj.
 * @property length Çözülen mesajın karakter uzunluğu (sunucunun gerçekten plaintext'i gördüğünü gösterir).
 */
@Serializable
data class SecureEchoResponse(
    val echo: String,
    val length: Int,
)

/**
 * Cihaz bütünlüğü raporu — istemci tarafı heuristik sinyaller (client→backend).
 *
 * Hard-block kararı [compromised] ile verilir: root / orijinal olmayan ROM / native güvenli
 * depolama (Keystore/Keychain) erişilemez ise cihaz "tehlikede" sayılır ve hem istemcide
 * (açılış gate) hem backend'de (korumalı endpoint'ler 403) engellenir.
 *
 * [emulator] ve [debuggerAttached] yalnızca **telemetri** — tek başlarına bloklamaz
 * (geliştirme/test cihazlarında yanlış-pozitif olmasın). Katmanlı modelde bu istemci raporu
 * "savunma derinliği"dir; KESİN karar backend'de Play Integrity / App Attest verdict'i ile verilir
 * (istemci raporu rootlu cihazca sahtelenebilir — bkz. [AttestationRequest]).
 *
 * @property keystoreAvailable Donanım-destekli güvenli depolama (Android Keystore / iOS Keychain)
 *   erişilebilir ve çalışır durumda mı. Cihazın orijinalliğiyle doğrudan ilişkili.
 * @property signatureValid Uygulama imza sertifikası beklenen değerle eşleşiyor mu (anti-repackage).
 */
@Serializable
data class DeviceIntegrityReport(
    val rooted: Boolean = false,
    val customRom: Boolean = false,
    val keystoreAvailable: Boolean = true,
    val emulator: Boolean = false,
    val debuggerAttached: Boolean = false,
    val signatureValid: Boolean = true,
    val platform: String = "",
    val checkedAtMillis: Long = 0L,
) {
    /** Hard-block koşulu: root, orijinal olmayan ROM, Keystore erişilemez ya da imza geçersiz (repackage). */
    val compromised: Boolean
        get() = rooted || customRom || !keystoreAvailable || !signatureValid
}

/**
 * Donanım-destekli attestation doğrulama isteği (client→backend).
 *
 * @property integrityToken Android: Play Integrity token; iOS: App Attest assertion (Base64).
 * @property nonce `GET /attestation/nonce` ile alınan tek-kullanımlık değer (replay önleme).
 * @property report İstemci heuristik raporu (savunma derinliği; opsiyonel).
 */
@Serializable
data class AttestationRequest(
    val platform: String,
    val integrityToken: String,
    val nonce: String,
    val report: DeviceIntegrityReport? = null,
)

/**
 * Attestation doğrulama yanıtı.
 *
 * @property ok Cihaz güvenilir kabul edildi mi (verdict REJECTED değilse true).
 * @property deviceTrust Sunucunun verdiği güven etiketi (örn. "trusted", "basic", "unevaluated", "rejected").
 */
@Serializable
data class AttestationResponse(
    val ok: Boolean,
    val deviceTrust: String,
)

/** `GET /attestation/nonce` yanıtı — tek-kullanımlık, kısa TTL'li attestation nonce'u. */
@Serializable
data class AttestationNonceResponse(
    val nonce: String,
)

// ─── Cihaz token senkronizasyonu (push hedefi) ─────────────────────────────────

/**
 * `POST /devices/token` gövdesi — kullanıcının bir cihazının güncel FCM token'ı.
 *
 * userId DAİMA JWT'den alınır (IDOR önlemi) → burada taşınmaz. [fcmToken] zorunlu;
 * [platform]/[installId] opsiyonel (geriye uyumlu, multi-device ayrımı için). Zengin cihaz
 * metadata'sı login anında [DeviceInfoDto] ile gider; bu DTO yalnızca token kayıt/rotasyon içindir.
 */
@Serializable
data class DeviceTokenRequest(
    val fcmToken: String,
    val platform: String? = null, // "android" | "ios"
    val installId: String? = null, // kuruluma özel UUID (DeviceInfoDto ile aynı semantik)
)

// ─── Admin push gönderimi ──────────────────────────────────────────────────────

/**
 * `POST /admin/notifications/send` gövdesi — manuel push. [ApiRoutes.HEADER_ADMIN_KEY] ile korunur.
 *
 * [target] hedefleme modunu seçer; ilgili alan dolu olmalı (bkz. `ContractValidation.validateAdminNotification`):
 *  - [NotificationTarget.user]      → [userId] VEYA [email] (biri zorunlu)
 *  - [NotificationTarget.users]     → [userIds] (boş olamaz)
 *  - [NotificationTarget.broadcast] → hedef alanı yok (tüm aktif cihazlar)
 *  - [NotificationTarget.segment]   → [city] VE/VEYA [professionKeys] (en az biri dolu olmalı)
 *
 * [title]/[body] gösterilecek bildirim; [data] sessiz/ek payload (örn. `{"deepLink":"kamuapp://..."}`).
 */
@Serializable
data class AdminNotificationRequest(
    val target: NotificationTarget,
    val title: String,
    val body: String,
    val userId: Long? = null,
    val email: String? = null,
    val userIds: List<Long> = emptyList(),
    val city: String? = null,
    val professionKeys: List<String> = emptyList(),
    val data: Map<String, String> = emptyMap(),
)

/**
 * Admin push gönderim sonucu özeti.
 *
 * @property sentCount FCM'in kabul ettiği gönderim sayısı (token başına; topic'te 0/1).
 * @property failedCount başarısız/ölü token sayısı.
 * @property targetType kullanılan [NotificationTarget] adı.
 * @property targetRef hedefin serbest gösterimi (userId / email / "N users" / topic / "broadcast").
 */
@Serializable
data class AdminNotificationResponse(
    val sentCount: Int,
    val failedCount: Int,
    val targetType: String,
    val targetRef: String? = null,
)

// ─── Admin web panel (key → token) ────────────────────────────────────────────

/**
 * `POST /admin/web/login` gövdesi — admin web panel girişi.
 *
 * [key] kullanıcı tarafından girilir; client'ta ASLA saklanmaz, yalnızca bu istekte yollanır.
 * Server env `ADMIN_WEB_KEYS` (veya dev fallback) ile sabit-zaman karşılaştırır.
 */
@Serializable
data class AdminWebLoginRequest(val key: String)

/**
 * Admin web giriş yanıtı.
 *
 * @property token kısa ömürlü imzalı admin JWT (`role=admin-web`). Sonraki isteklerde
 *   `Authorization: Bearer <token>` ile yollanır; SADECE bellekte tutulur (kalıcı depo yok).
 * @property admin giriş yapan admin adı (örn. "Adem") — panelde gösterim + audit.
 */
@Serializable
data class AdminWebLoginResponse(val token: String, val admin: String)

/** `GET /admin/web/verify` yanıtı — geçerli admin token'ın sahibi. */
@Serializable
data class AdminWebMeResponse(val admin: String)

// ─── Device heartbeat (Monster online/offline) ────────────────────────────────

/** `POST /device/heartbeat` gövdesi — [ApiRoutes.HEADER_ADMIN_KEY] DEĞİL, upload handshake ile korunur. */
@Serializable
data class DeviceHeartbeatRequest(val deviceName: String)

/** `GET /admin/web/monster-status` yanıtı. */
@Serializable
data class AdminWebMonsterStatusResponse(val online: Boolean, val lastPingAt: Long?)

/** `GET /admin/web/upload-status` yanıtı — cliApp'in son başarılı ilan yükleme (senkron) durumu. */
@Serializable
data class AdminWebUploadStatusResponse(val lastUploadAt: Long?, val lastNewCount: Int?)

/** `GET /admin/web/server-info` yanıtı — anlık CPU/RAM/disk kullanımı, kalıcı veri değil. */
@Serializable
data class AdminWebServerInfoResponse(
    val cpuLoadPercent: Double,
    val ramUsedBytes: Long,
    val ramTotalBytes: Long,
    val diskUsedBytes: Long,
    val diskTotalBytes: Long,
)

// ─── Admin web: bildirim geçmişi ──────────────────────────────────────────────

/** `GET /admin/web/notifications` satırı — [NotificationLog] audit kaydının wire karşılığı. */
@Serializable
data class NotificationLogEntry(
    val id: Long,
    val createdAt: Long,
    val targetType: String,
    val targetRef: String?,
    val title: String,
    val sentCount: Int,
    val failedCount: Int,
    val triggeredBy: String?,
    /**
     * Push gövdesi — kullanıcının cihazında GÖRDÜĞÜ metin. Eskiden saklanmıyordu (yalnızca
     * [title]); "bu bildirimde kaç ilan yazıyordu" sorusu gövdenin içinde olduğu için geri
     * okunamıyordu. Eski kayıtlarda `null`.
     */
    val body: String? = null,
    /**
     * Bildirimin DUYURDUĞU ilan sayısı — gövdedeki metinden ayrıştırmak yerine gönderim anında
     * ayrı kolona yazılır (metin değişse bile sayı bozulmaz). Yalnızca ilan kaynaklı
     * bildirimlerde dolu; hatırlatma/manuel admin gönderimlerinde ve eski kayıtlarda `null`.
     */
    val listingCount: Int? = null,
)

/** `GET /admin/web/notifications` yanıtı. */
@Serializable
data class NotificationLogListResponse(val notifications: List<NotificationLogEntry>)

/** `GET /admin/web/notifications/{id}/deliveries` satırı — kullanıcı bazlı teslim durumu. */
@Serializable
data class NotificationDeliveryItem(
    val userId: Long,
    val email: String,
    val platform: String?,
    val status: String,
    val createdAt: Long,
)

/** `GET /admin/web/notifications/{id}/deliveries` yanıtı. */
@Serializable
data class NotificationDeliveryListResponse(val deliveries: List<NotificationDeliveryItem>)

// ─── Admin web: destek chat (mobil ↔ admin, Firebase RTDB support_chats/ üzerinden) ──────────────

/**
 * `GET /admin/web/support-chats` satırı — bir kullanıcının destek chat özeti (RTDB
 * `support_chats/{uid}/meta` + node key'i olan [uid]'nin birleşimi).
 */
@Serializable
data class SupportChatSummaryDto(
    val uid: String,
    val displayName: String,
    val email: String,
    val lastMessageAt: Long,
    val lastMessagePreview: String,
    val unreadByAdmin: Boolean,
)

/**
 * Destek chat mesajı — mobil ↔ admin ortak wire formatı, RTDB `support_chats/{uid}/messages/{id}`
 * ile BİREBİR aynı alanlar. [senderType] `"user"` veya `"admin"`; admin için [senderId] literal `"admin"`.
 */
@Serializable
data class SupportMessageDto(
    val senderId: String,
    val senderType: String,
    val text: String,
    val timestamp: Long,
)

/** `GET /admin/web/support-chats` yanıtı. */
@Serializable
data class SupportChatSummaryListResponse(val chats: List<SupportChatSummaryDto>)

/** `GET /admin/web/support-chats/{uid}/messages` yanıtı — kronolojik (eskiden yeniye) sıralı. */
@Serializable
data class SupportChatMessagesResponse(val messages: List<SupportMessageDto>)

/** `POST /admin/web/support-chats/{uid}/reply` gövdesi — admin'in yazdığı cevap metni. */
@Serializable
data class SupportChatReplyRequest(val text: String)

// ── Veri Sağlayıcı (cliApp/Monster) çalışma telemetrisi ───────────────────────────────────
//
// cliApp zaten tipli `CollectorEvent` olayları üretiyor ve bunları YEREL diske (events.jsonl)
// yazıyordu; aşağıdaki sözleşme o olayları server'a akıtıp admin panelinde geçmişe dönük
// görüntülemeyi sağlar. Tasarım kararı: cliApp HAM olayları gönderir, adım kartları (faz
// özetleri) server'da olaylardan TÜRETİLİR — böylece cliApp'teki `reduce()` mantığı
// ikinci kez yazılmaz (tek kaynak: olay akışı).

/** Bir toplama döngüsünün nihai durumu. */
@Serializable
enum class CollectorRunStatus {
    /** Başladı, henüz `finish` gelmedi (veya cliApp çöktü — bkz. bayat çalışma süpürücüsü). */
    RUNNING,

    /** exitCode == 0 — tüm kaynaklar sorunsuz. */
    SUCCESS,

    /** exitCode != 0 ama ilan üretildi — kaynaklardan biri kısmen başarısız (örn. enrich hatası). */
    PARTIAL,

    /** Hiç ilan üretilemedi veya çalışma hata ile düştü. */
    FAILED,
}

/** Olay önem seviyesi — cliApp `Level` enum'u ile birebir. */
@Serializable
enum class CollectorEventLevel { INFO, WARN, ERROR }

/** Bir ilanın bu döngüde ulaştığı EN İLERİ aşama. */
@Serializable
enum class CollectorListingStage {
    /** Kaynaktan (KK API / İŞKUR PDF) çekildi. */
    FETCHED,

    /** Yapay zekâ ile yapılandırıldı (enrich). */
    ENRICHED,

    /** Server'a yüklendi. */
    UPLOADED,

    /** Bu ilan için adımlardan biri başarısız oldu — [CollectorRunListingDto.errorDetail] dolu. */
    FAILED,
}

/** `POST /collector/runs` gövdesi — döngü başlangıcını duyurur. */
@Serializable
data class CollectorRunStartRequest(
    val runId: String,
    val deviceName: String,
    val startedAt: Long,
)

/**
 * Tek bir toplama olayı — cliApp'in `events.jsonl` satırının tel (wire) karşılığı.
 *
 * @param seq cliApp içinde 0'dan artan sıra numarası; tekrar gönderimde tekilleştirme anahtarı
 *   `(runId, seq)`. Böylece ağ hatasında batch yeniden denenebilir (idempotent).
 * @param detail olayın değişken alanları düzleştirilmiş halde (örn. `count=28`, `guid=...`).
 *   Heterojen tipleri tel üzerinde taşımamak için değerler metne çevrilir.
 */
@Serializable
data class CollectorEventDto(
    val seq: Int,
    val ts: Long,
    val source: String,
    val phase: String,
    val event: String,
    val level: CollectorEventLevel,
    val detail: Map<String, String> = emptyMap(),
    val durationMs: Long? = null,
)

/** `POST /collector/runs/{runId}/events` gövdesi. */
@Serializable
data class CollectorEventsRequest(val events: List<CollectorEventDto>)

/**
 * Bir ilanın bu döngüdeki izi — 3. seviye detay ekranını besler.
 *
 * Not: İlanın TAM içeriği burada tutulmaz (canlı `Listings` tablosunda zaten var); yalnızca
 * "hangi ilan çekildi/yüklendi" sorusunu tek başına yanıtlayacak kadar alan denormalize edilir —
 * ilan sonradan arşivlense bile geçmiş çalışma kaydı okunabilir kalsın diye.
 */
@Serializable
data class CollectorRunListingDto(
    val listingKey: String,
    val source: String,
    val stage: CollectorListingStage,
    val institutionName: String? = null,
    val city: String? = null,
    val occupation: String? = null,
    val quotaCount: Int? = null,
    val applyUrl: String? = null,
    /** Bu döngüde ilk kez görülen ilan mı (özet satırındaki "N yeni ilan" sayacı). */
    val isNew: Boolean = false,
    val errorDetail: String? = null,
)

/** `POST /collector/runs/{runId}/listings` gövdesi — parça parça gönderilebilir. */
@Serializable
data class CollectorRunListingsRequest(val listings: List<CollectorRunListingDto>)

/** `POST /collector/runs/{runId}/finish` gövdesi — nihai sayaçlar cliApp'in `RunState`'inden gelir. */
@Serializable
data class CollectorRunFinishRequest(
    val finishedAt: Long,
    val exitCode: Int,
    val kkListings: Int,
    /** İŞKUR **kamu memur** bölümünden gelen ilan sayısı. */
    val iskurListings: Int,
    /**
     * İŞKUR **kurum dışı kamu işçi** bölümünden gelen ilan sayısı ([ListingType.KURUM_DISI]).
     * Eski toplayıcı bu alanı göndermez → `null` ("bu sürüm kurum dışı toplamıyor"), 0 ise
     * "topladı ama hiç ilan yoktu". Ayrım panelde anlamlıdır, bu yüzden nullable.
     */
    val kurumDisiListings: Int? = null,
    val totalListings: Int,
    val uploadedCount: Int? = null,
    val newCount: Int? = null,
    val errorDetail: String? = null,
    /**
     * **AI'YA GİRMEDEN ÖNCE** kaynaktan çekilen HAM ilan/duyuru sayısı — Kariyer Kapısı arama
     * sonucundaki ilan sayfası adedi. [kkListings] ise AI'nın O sayfalardan ÜRETTİĞİ ilan
     * sayısıdır; ikisi aynı birim DEĞİLDİR (bir sayfa alt-ilanlar üzerinden yüzlerce ilan
     * üretebilir). "CLI kaç ilan çekti → AI kaç ilan çıkardı" karşılaştırması bu ayrıma dayanır.
     * Eski toplayıcı göndermez → `null` ("bilinmiyor", 0 ile karıştırılmamalı).
     */
    val fetchedKk: Int? = null,
    /** AI öncesi ham satır sayısı — İŞKUR kamu memur bölümü il sayfalarındaki toplam duyuru satırı. */
    val fetchedIskur: Int? = null,
    /** AI öncesi ham satır sayısı — İŞKUR kurum dışı kamu işçi bölümü. */
    val fetchedKurumDisi: Int? = null,
    /**
     * AI öncesi ham belge/satır sayısı toplamı ([fetchedKk] + [fetchedIskur] + [fetchedKurumDisi]).
     * Ayrıca gönderilir ki panel eski kayıtlarda kırılım olmadan da toplamı gösterebilsin.
     */
    val fetchedTotal: Int? = null,
    /**
     * AI aşamasına HİÇ girmeden sessizce düşen kaynak birimi sayısı — PDF'i olmayan İŞKUR
     * satırları, guid'siz KK arama kayıtları ve mükerrer PDF'ler. Eskiden hiçbir yerde
     * sayılmıyordu; "çekilen 45 ama AI'ya 38 gitti" farkının açıklaması budur.
     */
    val droppedBeforeAi: Int? = null,
    // ── AI token tüketimi ve maliyeti (ölçüm bu sürümden itibaren başlar) ──
    // Hepsi NULLABLE: `null` = "bu toplayıcı sürümü ölçmüyor" (eski koşular), 0 = "ölçtü, sıfırdı"
    // (ör. tüm sayfalar PageCache'ten geldi, AI hiç çağrılmadı). Panel bu ayrımı gösterir.
    /** Bu döngüde AI'ya gönderilen (girdi) token toplamı. */
    val aiPromptTokens: Long? = null,
    /** Bu döngüde AI'dan dönen (çıktı) token toplamı. */
    val aiCompletionTokens: Long? = null,
    /** [aiPromptTokens] + [aiCompletionTokens]; kırılım gelmese de toplam gösterilebilsin diye ayrı. */
    val aiTotalTokens: Long? = null,
    /** Bu döngüde yapılan AI çağrısı adedi (cache isabetleri DAHİL DEĞİL — onlar AI'ya gitmez). */
    val aiCallCount: Long? = null,
    /**
     * Token'lardan hesaplanan tahmini maliyet, **USD**. Fiyat tablosu cliApp'te (`AiPricing`) tutulur
     * ve env ile geçersiz kılınabilir. Fiyatı bilinmeyen sağlayıcıda (ör. AGY iç endpoint'i) `0.0`
     * gelir — bu "bedava" demek DEĞİL, "fiyat bilinmiyor" demektir; token sayısı yine anlamlıdır.
     */
    val aiCostUsd: Double? = null,
)

/**
 * Liste öğesi özeti — "15:00'da başladı, 906 ilan, 200 yeni, başarılı".
 * Detay ekranına girmeden görülmesi gereken her şey burada.
 */
@Serializable
data class CollectorRunSummaryDto(
    val runId: String,
    val deviceName: String,
    val startedAt: Long,
    val finishedAt: Long? = null,
    val durationMs: Long? = null,
    val status: CollectorRunStatus,
    val exitCode: Int? = null,
    val totalListings: Int = 0,
    val kkListings: Int = 0,
    val iskurListings: Int = 0,
    val kurumDisiListings: Int? = null,
    /** AI öncesi ham KK ilan sayfası sayısı; eski kayıtlarda `null`. Bkz. [CollectorRunFinishRequest.fetchedKk]. */
    val fetchedKk: Int? = null,
    /** AI öncesi ham İŞKUR (memur) duyuru satırı sayısı; eski kayıtlarda `null`. */
    val fetchedIskur: Int? = null,
    /** AI öncesi ham İŞKUR (kurum dışı) duyuru satırı sayısı; eski kayıtlarda `null`. */
    val fetchedKurumDisi: Int? = null,
    /** AI öncesi ham belge/satır toplamı; eski kayıtlarda `null`. */
    val fetchedTotal: Int? = null,
    /** AI'ya hiç girmeden sessizce düşen kaynak birimi sayısı; eski kayıtlarda `null`. */
    val droppedBeforeAi: Int? = null,
    val newCount: Int? = null,
    val uploadedCount: Int? = null,
    val errorCount: Int = 0,
    val warnCount: Int = 0,
    /** Bu döngüde AI'ya gönderilen (girdi) token toplamı; ölçmeyen sürümde `null`. */
    val aiPromptTokens: Long? = null,
    /** Bu döngüde AI'dan dönen (çıktı) token toplamı; ölçmeyen sürümde `null`. */
    val aiCompletionTokens: Long? = null,
    /** Bu döngünün toplam token tüketimi; ölçmeyen sürümde `null`. */
    val aiTotalTokens: Long? = null,
    /** Bu döngüde yapılan AI çağrısı adedi; ölçmeyen sürümde `null`. */
    val aiCallCount: Long? = null,
    /** Tahmini maliyet (USD); fiyatı bilinmeyen sağlayıcıda `0.0`, ölçmeyen sürümde `null`. */
    val aiCostUsd: Double? = null,
)

/** `GET /admin/web/collector/runs` yanıtı — yeniden eskiye sıralı. */
@Serializable
data class CollectorRunListResponse(
    val runs: List<CollectorRunSummaryDto>,
    val hasMore: Boolean = false,
)

/**
 * Detay ekranındaki bir adım kartı — "İŞKUR / il-sayfaları: 81 ilden 50'si, 13,1 sn".
 * Server tarafında olay akışından türetilir; cliApp bunu GÖNDERMEZ.
 *
 * @param inputCount adıma giren birim sayısı ("81'den"); bilinmiyorsa null.
 * @param okCount başarıyla işlenen ("50'si"). [inputCount] ile AYNI birimdedir — oran olarak
 *   gösterilebilir olması buna bağlıdır.
 * @param producedCount adımın ÜRETTİĞİ kayıt sayısı; [okCount]'tan farklı bir birimdir.
 *   Yapay zekâ (enrich) adımında girdi BELGE, çıktı İLAN'dır: 28 sayfadan 27'si başarıyla
 *   işlenip 855 ilan üretilebilir. Bu iki sayıyı tek orana sıkıştırmak yanlış olur.
 */
@Serializable
data class CollectorPhaseDto(
    val source: String,
    val phase: String,
    val startedAt: Long,
    val durationMs: Long? = null,
    val level: CollectorEventLevel = CollectorEventLevel.INFO,
    val inputCount: Int? = null,
    val okCount: Int? = null,
    val failedCount: Int? = null,
    val cacheHits: Int? = null,
    val producedCount: Int? = null,
    val note: String? = null,
)

/** `GET /admin/web/collector/runs/{runId}` yanıtı. */
@Serializable
data class CollectorRunDetailResponse(
    val summary: CollectorRunSummaryDto,
    val phases: List<CollectorPhaseDto>,
    val events: List<CollectorEventDto>,
    val listingCount: Int = 0,
)

/** `GET /admin/web/collector/runs/{runId}/listings` yanıtı. */
@Serializable
data class CollectorRunListingsResponse(
    val listings: List<CollectorRunListingDto>,
    val hasMore: Boolean = false,
)

// ─── Admin web: "Veri ve Bildirim" paneli (ilan → AI → senkron → bildirim zinciri) ────────────

/**
 * Panelin üst özetindeki bir eğitim düzeyi kartı.
 *
 * İki farklı sayı taşır ve bunlar KASITLI olarak farklıdır:
 * - [taggedCount]: bu düzeye AÇIKÇA etiketlenmiş ilan sayısı (`listingEducationLevels` köprüsü).
 *   Düzeylerin toplamı genel toplamı VERMEZ — bir ilan birden çok düzeye etiketlenebilir.
 * - [visibleCount]: o düzeydeki bir kullanıcının uygulamada GERÇEKTEN gördüğü ilan sayısı;
 *   `ListingsByEducationRepository.readByLevel` ile birebir aynı kural (düzey etiketi + tüm
 *   kurum dışı ilanlar + "farketmez" ilanlar; `primary`/`middleSchool` → `highSchool`).
 * Bildirim hedef kitlesi [visibleCount] mantığını izler, bu yüzden ikisi de gösterilir.
 *
 * @param notificationCount bu düzeye giden bildirim adedi (audit kaydından).
 */
@Serializable
data class PipelineLevelTotalDto(
    val level: EducationLevel,
    val taggedCount: Int,
    val visibleCount: Int,
    val notificationCount: Int,
)

/** `GET /admin/web/pipeline/overview` yanıtı — panelin en üstündeki genel toplam bloğu. */
@Serializable
data class PipelineOverviewResponse(
    /** Şu an aktif (arşivlenmemiş) toplam ilan sayısı — düzey kırılımlarının toplamı DEĞİL. */
    val totalListings: Int,
    /** AI'nın "öğrenim şartı yok" (farketmez) diye teyit ettiği ilan sayısı — her düzeye görünür. */
    val unspecifiedCount: Int,
    /** Kurum dışı (İŞKUR işçi alımı) ilan sayısı — eğitim düzeyinden bağımsız, her düzeye görünür. */
    val kurumDisiCount: Int,
    /** Hiçbir düzeye etiketlenmemiş VE farketmez de olmayan ilan sayısı — sınıflandırma boşluğu göstergesi. */
    val unclassifiedCount: Int,
    val levels: List<PipelineLevelTotalDto>,
    /** Toplam bildirim adedi (her tetikleyici dahil). */
    val totalNotifications: Int,
    /** Yalnızca yeni-ilan kaynaklı (`triggeredBy = "listing"`) bildirim adedi. */
    val listingNotifications: Int,
    val lastIngestAt: Long? = null,
    val lastNotificationAt: Long? = null,
    /**
     * **Kümülatif AI tüketimi** — panelin sağ üst rozeti. `collectorRuns` tablosundaki TÜM
     * koşuların toplamı; ölçüm bu sürümle başladığı için eski koşular toplama 0 katkı verir.
     * [measuredRunCount] kaç koşunun gerçekten ölçüldüğünü söyler — "toplam düşük görünüyor"
     * sorusunun cevabı budur, sessizce yanıltmaz.
     */
    val aiTotalTokens: Long = 0,
    val aiPromptTokens: Long = 0,
    val aiCompletionTokens: Long = 0,
    val aiCallCount: Long = 0,
    /** Tahmini kümülatif maliyet (USD). Fiyatı bilinmeyen sağlayıcının katkısı 0'dır. */
    val aiCostUsd: Double = 0.0,
    /** Token ölçümü İÇEREN koşu sayısı — kümülatif toplamın kapsamını gösterir. */
    val measuredRunCount: Int = 0,
)

/**
 * Bir senkronun tek eğitim düzeyi için kırılımı.
 *
 * @param newCount bu senkronda O düzeye etiketlenerek EKLENEN ilan sayısı ("+3").
 * @param totalAfter senkron BİTTİKTEN sonra o düzeydeki toplam etiketli ilan sayısı ("→ 412").
 *   Ardışık iki satırın `totalAfter` farkı, aradaki net değişimi (ekleme − arşivleme) verir.
 * @param notified bu düzeydeki kullanıcılara bu senkron için bildirim gitti mi.
 */
@Serializable
data class PipelineIngestLevelDto(
    val level: EducationLevel,
    val newCount: Int,
    val totalAfter: Int,
    val notified: Boolean = false,
)

/**
 * Zaman çizelgesinin bir satırı — TEK bir `POST /uploads` senkronu ve onun doğurduğu bildirim.
 *
 * Zincir: toplayıcı [fetchedTotal] ham belge çekti → AI [aiTotal] ilan üretti → sunucuya
 * [receivedCount] aktif ilan geldi → [newCount] yeni eklendi, [archivedCount] düşenler
 * arşivlendi → toplam [totalAfter] oldu ve [completedAt] yazıldı → [notificationId] bildirimi gitti.
 * Toplayıcı alanları yalnızca `POST /uploads` `X-Run-Id` header'ı ile geldiyse doludur.
 */
@Serializable
data class PipelineIngestDto(
    val id: Long,
    val createdAt: Long,
    val runId: String? = null,
    // ── toplayıcı (cliApp) tarafı; runId eşleşmezse null ──
    val fetchedTotal: Int? = null,
    val fetchedKk: Int? = null,
    val fetchedIskur: Int? = null,
    val fetchedKurumDisi: Int? = null,
    val droppedBeforeAi: Int? = null,
    val aiTotal: Int? = null,
    /** Bu turun AI token tüketimi (girdi+çıktı); ölçmeyen sürümde `null`. */
    val aiTotalTokens: Long? = null,
    /** Bu turda yapılan AI çağrısı adedi; ölçmeyen sürümde `null`. */
    val aiCallCount: Long? = null,
    /** Bu turun tahmini AI maliyeti (USD); fiyatı bilinmeyen sağlayıcıda `0.0`. */
    val aiCostUsd: Double? = null,
    // ── sunucu senkronu ──
    /** Yüklenen dosyadaki, son başvuru tarihi geçmemiş (aktif) ilan sayısı. */
    val receivedCount: Int,
    val newCount: Int,
    /** Serbest metin tablo sınırına kırpıldıysa etkilenen alan sayısı; eski kayıtlarda `null`. */
    val truncatedFieldCount: Int? = null,
    /** "Farketmez" olarak eklenen yeni ilan sayısı — [levels] kırılımına GİRMEZ (somut düzeyi yok). */
    val unspecifiedNewCount: Int,
    /** Bu senkronda kaynakta artık görünmediği için arşive taşınan ilan sayısı. */
    val archivedCount: Int,
    /**
     * Anahtarı kaymış ama anlamsal olarak AYNI olan ilan sayısı — "yeni" SAYILMAYAN, kurtarılan
     * ilanlar. Sıfırdan büyükse AI aynı belge için farklı metin üretiyor demektir; bu sayı
     * olmasaydı kurtarma sessiz kalır ve sorunun sürdüğü fark edilmezdi.
     */
    val keyDriftCount: Int = 0,
    /** Senkron bittikten sonraki toplam aktif ilan sayısı — panelin "genel toplam" sütunu. */
    val totalAfter: Int,
    /** `null`, senkronun başlamış ancak sunucu hatasıyla tamamlanamamış olduğunu gösterir. */
    val completedAt: Long? = null,
    val levels: List<PipelineIngestLevelDto> = emptyList(),
    // ── bildirim ──
    val notificationId: Long? = null,
    val notificationTitle: String? = null,
    val notificationBody: String? = null,
    /** Bildirim gövdesinde duyurulan ilan sayısı. */
    val notificationListingCount: Int? = null,
    val notificationSentCount: Int? = null,
    val notificationFailedCount: Int? = null,
    /** Bildirimin gittiği eğitim düzeyleri (audit `targetRef`). */
    val notificationTargetRef: String? = null,
    /**
     * Yeni ilan olduğu hâlde bildirim GÖNDERİLMEDİYSE sebebi (örn. toplu yeniden anahtarlama
     * şüphesi). `null` + [notificationId] `null` + [newCount] `0` → gönderilecek bir şey yoktu.
     */
    val notificationSuppressedReason: String? = null,
)

/** `GET /admin/web/pipeline/ingests` yanıtı — yeniden eskiye sıralı, sayfalı. */
@Serializable
data class PipelineIngestListResponse(
    val ingests: List<PipelineIngestDto>,
    val hasMore: Boolean = false,
)

/**
 * `GET /admin/web/pipeline/ingests/{ingestId}/listings` satırı — o senkronda EKLENEN tek bir ilan.
 *
 * @param createdAt ilanın sunucu veritabanına yazıldığı an (epoch ms) — "ne zaman, saat kaçta geldi".
 * @param collectedAt cliApp'in bu ilanı topladığı an (epoch ms); eski kayıtlarda `null`.
 *   Mobil istemciye ASLA gönderilmez, yalnızca bu admin yüzeyinde görünür.
 */
@Serializable
data class PipelineListingDto(
    val listingKey: String,
    val source: String,
    val institutionName: String? = null,
    val type: ListingType = ListingType.KURUM_ICI,
    val createdAt: Long,
    val collectedAt: Long? = null,
    val educationLevels: List<EducationLevel> = emptyList(),
    val educationLevelUnspecified: Boolean = false,
    val applicationEndDate: String? = null,
)

/** `GET /admin/web/pipeline/ingests/{ingestId}/listings` yanıtı. */
@Serializable
data class PipelineListingListResponse(
    val listings: List<PipelineListingDto>,
    val hasMore: Boolean = false,
)

/** `GET /admin/web/pipeline/levels/{level}/notifications` yanıtı — o düzeye giden bildirimler. */
@Serializable
data class PipelineLevelNotificationsResponse(
    val level: EducationLevel,
    val notifications: List<NotificationLogEntry>,
    val hasMore: Boolean = false,
)

// ─── Herkese açık tanıtım sitesi (auth'suz) ───────────────────────────────────────────────────

/**
 * `GET /api/v1/public/listing-stats` yanıtı — tanıtım sitesinin ana sayfasındaki üçlü sayaç.
 *
 * **Yalnızca sayı taşır.** Kurum adı, `uniqValue`, başvuru linki gibi HİÇBİR ilan alanı bu DTO'da
 * yoktur ve eklenmemelidir: uç auth'suz olduğu için içerik eklemek, `X-Web-Key` ile korunan
 * [ApiRoutes.WEB_LISTINGS]'i anlamsız kılar.
 *
 * Sayılan küme mobil `GET /listings` misafir dalıyla AYNI: arşivlenmemiş ve son başvuru tarihi
 * geçmemiş ilanlar. [total] = [kurumIci] + [kurumDisi] (ilan tipi NOT NULL + default'lu tek kolon).
 *
 * @property total tüm aktif ilanlar.
 * @property kurumIci kamu kadro alımı ilanları ([ListingType.KURUM_ICI]).
 * @property kurumDisi kurum dışı (İŞKUR) kamu işçi alımı ilanları ([ListingType.KURUM_DISI]).
 */
@Serializable
data class PublicListingStatsResponse(
    val total: Int,
    val kurumIci: Int,
    val kurumDisi: Int,
)

// ─── Telemetri: tanıtım sitesi ziyaretleri + mobil reklam gösterimleri ────────────────────────

/**
 * Tanıtım sitesinden gelebilecek olay türleri — **kapalı beyaz liste**.
 *
 * Enum olması bilinçli: uç auth'suz olduğu için serbest metin bir `eventType` kabul etmek, DB'yi
 * herkesin yazabildiği bir metin çöplüğüne çevirirdi. Tanınmayan değer serileştirmede düşer →
 * `StatusPages` 400'e çevirir; ayrıca doğrulama koduna ihtiyaç kalmaz.
 */
@Serializable
enum class WebEventType {
    /** Sayfa açılışı — oturum (sekme) başına BİR kez gönderilir. */
    @SerialName("page_view")
    PAGE_VIEW,

    /** Mağaza linkine tıklama; [WebEventRequest.target] hangi mağaza olduğunu söyler. */
    @SerialName("store_click")
    STORE_CLICK,

    /**
     * **Mobil** uygulamada bir reklam gösterimi (impression); `adFormat` hangi format olduğunu
     * söyler. Web olaylarıyla aynı tabloda durur ama özet çıktısında AYRI alanlarda raporlanır —
     * ziyaret sayısıyla reklam gösterimi asla toplanmaz.
     */
    @SerialName("ad_impression")
    AD_IMPRESSION,
}

/** [WebEventType.STORE_CLICK] hedefi — beyaz liste ([WebEventType] ile aynı gerekçe). */
@Serializable
enum class WebStoreTarget {
    @SerialName("app_store")
    APP_STORE,

    @SerialName("play_store")
    PLAY_STORE,
}

/**
 * `POST /api/v1/public/web-events` gövdesi.
 *
 * **Gizlilik:** burada kişi tanımlayıcı alan YOKTUR ve eklenmemelidir. [sessionId] istemcinin
 * ürettiği rastgele bir değerdir (`sessionStorage`, sekme kapanınca kaybolur) — çerez değildir,
 * kullanıcıyı ziyaretler arasında izlemez; yalnız "aynı ziyaret" tekilleştirmesi için vardır.
 * IP hiç gönderilmez; sunucu onu tuzlayıp hash'ler (ham IP saklanmaz).
 *
 * @property eventType olay türü (beyaz liste).
 * @property target yalnız [WebEventType.STORE_CLICK] için dolu; diğer türlerde null olmalı.
 * @property sessionId sekme ömürlü rastgele değer; en fazla [ContractValidation.WEB_SESSION_ID_MAX] karakter.
 * @property referrer yönlendiren sayfa — istemci sorgu/parça kısmını atarak yollar (izleme parametresi sızmasın).
 */
@Serializable
data class WebEventRequest(
    val eventType: WebEventType,
    val target: WebStoreTarget? = null,
    val sessionId: String? = null,
    val referrer: String? = null,
)

/**
 * Tek bir günün (Türkiye saatiyle) telemetri kırılımı.
 *
 * @property date `YYYY-MM-DD`, **TR günü** (UTC+3) — UTC günü kullanılsaydı gece yarısı ile 03:00
 *   arasındaki ziyaretler panelde bir önceki güne yazılırdı.
 * @property uniqueSessions o gün en az bir olay üreten farklı `sessionId` sayısı.
 */
@Serializable
data class WebTelemetryDayDto(
    val date: String,
    val pageViews: Int,
    val uniqueSessions: Int,
    val appStoreClicks: Int,
    val playStoreClicks: Int,
    /**
     * O günkü mobil reklam gösterimlerinin formata göre kırılımı. Gösterim OLMAYAN format anahtar
     * olarak da bulunmaz → gün toplamı `adImpressionsByFormat.values.sum()`. Ayrı bir toplam alanı
     * BİLEREK yok: iki kaynak zamanla birbirinden ayrı düşerdi.
     */
    val adImpressionsByFormat: Map<AdImpressionFormat, Int> = emptyMap(),
)

/**
 * `GET /api/v1/admin/web/web-telemetry` yanıtı.
 *
 * [days] pencerenin TÜM günlerini içerir (olay olmayan gün sıfırlarla doldurulur), eskiden yeniye
 * sıralı — panel grafiği/listesi boşluklu olmasın. Toplamlar pencerenin tamamı içindir; [totalUniqueSessions]
 * günlük değerlerin toplamı DEĞİLDİR (aynı oturum iki güne yayılırsa pencere genelinde bir kez sayılır).
 */
@Serializable
data class WebTelemetrySummaryResponse(
    val days: List<WebTelemetryDayDto>,
    val totalPageViews: Int,
    val totalUniqueSessions: Int,
    val totalAppStoreClicks: Int,
    val totalPlayStoreClicks: Int,
    /** Pencere genelinde formata göre reklam gösterimi; toplam = `values.sum()` ([WebTelemetryDayDto] ile aynı gerekçe). */
    val totalAdImpressionsByFormat: Map<AdImpressionFormat, Int> = emptyMap(),
)

// ─── Mobil reklam gösterimi (impression) telemetrisi ──────────────────────────────────────────

/**
 * Sayılan reklam formatları — [wireName]'ler monetization SDK'sındaki `AdFormat.wireName` ile
 * birebir aynıdır, böylece mobil taraf format çevirisini tek satırda ([fromWire]) yapar.
 *
 * NOT (kapsam): monetization SDK'sı native reklamlar için gösterim geri çağrısı YAYINLAMAZ
 * (`NativeAdBridge`'de `onImpression` yok) — bu yüzden [NATIVE] şu an pratikte hiç sayılmaz.
 * Enum'da yine de var: köprüye geri çağrı eklendiği gün sözleşme değişmeden çalışsın.
 */
@Serializable
enum class AdImpressionFormat(val wireName: String) {
    @SerialName("banner")
    BANNER("banner"),

    @SerialName("interstitial")
    INTERSTITIAL("interstitial"),

    @SerialName("rewarded")
    REWARDED("rewarded"),

    @SerialName("rewarded_interstitial")
    REWARDED_INTERSTITIAL("rewarded_interstitial"),

    @SerialName("app_open")
    APP_OPEN("app_open"),

    @SerialName("native")
    NATIVE("native"),
    ;

    companion object {
        /** Wire adından format; tanınmayan ad için null (çağıran loglayıp atlar). */
        fun fromWire(name: String): AdImpressionFormat? = entries.firstOrNull { it.wireName == name }
    }
}

/**
 * Tek bir reklam gösterimi.
 *
 * @property shownAt gösterimin CİHAZ saatiyle epoch ms'i. Toplu gönderim yüzünden istek anından
 *   dakikalar önce olabilir; sunucu makul bir pencereye kırpar (bozuk cihaz saati günlük kırılımı
 *   zehirlemesin).
 */
@Serializable
data class AdImpressionItem(
    val format: AdImpressionFormat,
    val shownAt: Long,
)

/**
 * `POST /api/v1/telemetry/ad-impressions` gövdesi — **toplu** gönderim.
 *
 * Tek tek gönderim bilinçli olarak desteklenmez: bir oturumda onlarca banner gösterimi olur ve her
 * biri için ayrı HTTP isteği pil + veri yakardı. İstemci cihazda biriktirir, periyodik olarak ve
 * uygulama arka plana geçerken tek istekte yollar.
 *
 * Uç kullanıcı JWT'si ile korunur: auth'suz olsaydı herkes panele istediği reklam sayısını
 * bastırabilirdi. Premium kullanıcıya reklam gösterilmediği için sayılar doğal olarak yalnız
 * ücretsiz kullanıcıları kapsar; giriş yapmamış (misafir) kullanıcıların gösterimleri de sayılmaz.
 */
@Serializable
data class AdImpressionBatchRequest(
    val impressions: List<AdImpressionItem>,
)

// ─── Admin: Analitik ekranı ───────────────────────────────────────────────────────────────────
//
// Bu bloğun TEK kaynağı kendi veritabanımızdır. Firebase Analytics'e giden olaylar
// (core:analytics → core:firebase) buradan OKUNAMAZ: Firebase Analytics olay verisi yalnız
// GA4 Data API (service account + property id) veya BigQuery export ile okunur; ikisi de bu
// projede kurulu DEĞİL. Bu yüzden panel, gerçekten sahip olduğumuz veriyi gösterir.

/** Bir kırılım satırı — `key` boş/bilinmeyen olabildiği için etiketleme istemcide yapılır. */
@Serializable
data class AnalyticsBreakdownEntry(
    /** Ham değer; kaynakta null ise boş string ("bilinmiyor" olarak sunulur). */
    val key: String,
    val count: Int,
)

/**
 * Pencere içindeki TEK bir günün (Türkiye saatiyle) sayaçları.
 *
 * @property date TR gününün ISO tarihi (`yyyy-MM-dd`).
 */
@Serializable
data class AnalyticsDayDto(
    val date: String,
    /** O gün oluşturulan hesap sayısı (`users.createdAt`). */
    val newUsers: Int,
    /** O günkü login/refresh olayı sayısı (`login_events`) — ziyaret değil, oturum açma. */
    val logins: Int,
    /** O gün login olan BENZERSİZ kullanıcı sayısı (günlük aktif kullanıcı). */
    val activeUsers: Int,
    /** O gün yapılan başvuru sayısı (`applications.appliedAt`). */
    val applications: Int,
    /** O gün eklenen favori sayısı (`favorites.createdAt`). */
    val favorites: Int,
)

/**
 * Kullanıcı tabanı özeti. Aktiflik `users.lastLoginAt` penceresinden türer — uygulamayı açıp
 * hiç istek atmayan kullanıcıyı SAYMAZ; elimizdeki en yakın ölçüm budur.
 */
@Serializable
data class AnalyticsUsersDto(
    val total: Int,
    val activeToday: Int,
    val active7d: Int,
    val active30d: Int,
    /** Seçili pencerede oluşturulan hesap sayısı. */
    val newInWindow: Int,
    /** Hiç giriş yapmamış hesaplar (`lastLoginAt` null) — kayıt sonrası düşen huni ayağı. */
    val neverLoggedIn: Int,
)

/** Ürün etkileşimi — toplam ve pencere içi ayrı verilir (biri stok, diğeri akış). */
@Serializable
data class AnalyticsEngagementDto(
    val applicationsTotal: Int,
    val applicationsInWindow: Int,
    val favoritesTotal: Int,
    val favoritesInWindow: Int,
    val remindersTotal: Int,
    val remindersInWindow: Int,
    /** Profil doldurmuş kullanıcı sayısı (`profiles` 1:1). */
    val profilesTotal: Int,
)

/**
 * Push teslim başarısı (`notificationDelivery`) — pencere içi. Oran istemcide hesaplanır;
 * `sent + failed == 0` iken "0" değil "veri yok" gösterilmelidir.
 */
@Serializable
data class AnalyticsNotificationsDto(
    val sentInWindow: Int,
    val failedInWindow: Int,
)

/**
 * Premium tabanı. `activeSubscriptions` mağaza doğrulamalı ACTIVE kayıtların BENZERSİZ kullanıcı
 * sayısıdır; `adminPremiumUsers` ödemesiz verilen erişimdir (`users.adminPremium`) ve gelir DEĞİLDİR.
 */
@Serializable
data class AnalyticsPremiumDto(
    val activeSubscriptions: Int,
    val adminPremiumUsers: Int,
)

/**
 * `GET /api/v1/admin/web/analytics` yanıtı — Analitik ekranının TEK veri kaynağı.
 *
 * `web` alanı, mevcut [WebTelemetrySummaryResponse] özetinin AYNISIDIR: tanıtım sitesi
 * telemetrisi ikinci kez hesaplanmaz, `WebEventRepository.summary` yeniden kullanılır.
 */
@Serializable
data class AdminAnalyticsResponse(
    /** Pencere genişliği (gün) — istemcinin gönderdiği `days` sunucuda kırpılmış hâli. */
    val windowDays: Int,
    /** Eskiden yeniye günlük seri; son eleman "bugün" (TR). */
    val days: List<AnalyticsDayDto>,
    val users: AnalyticsUsersDto,
    val engagement: AnalyticsEngagementDto,
    val notifications: AnalyticsNotificationsDto,
    val premium: AnalyticsPremiumDto,
    /** `users.lastPlatform` kırılımı (android / ios / bilinmiyor). */
    val platforms: List<AnalyticsBreakdownEntry>,
    /** `users.lastAppVersion` kırılımı — sürüm benimseme oranı. */
    val appVersions: List<AnalyticsBreakdownEntry>,
    /** `users.lastCountry` kırılımı. */
    val countries: List<AnalyticsBreakdownEntry>,
    /** `users.lastCity` kırılımı (ilk N). */
    val cities: List<AnalyticsBreakdownEntry>,
    /** `users.authProvider` kırılımı — kayıt yöntemi (email / google / apple). */
    val authProviders: List<AnalyticsBreakdownEntry>,
    /** `profiles.educationLevel` kırılımı — ilan hedeflemesinin taban dağılımı. */
    val educationLevels: List<AnalyticsBreakdownEntry>,
    /** Tanıtım sitesi + mobil reklam telemetrisi (mevcut özet ucunun aynısı). */
    val web: WebTelemetrySummaryResponse,
)

// ─── Mobil çökme (crash) telemetrisi ──────────────────────────────────────────────────────────
//
// Crashlytics uygulamada KURULU (GitLive KMP SDK + Gradle plugin) ama okunabilir bir public REST
// API'si YOK — konsol ya da BigQuery export dışında veri çekilemez. Admin panelinin çökmeleri
// gösterebilmesi için bu hafif kendi hattımız var: cihaz çökme anında diske yazar, BİR SONRAKİ
// açılışta buraya gönderir.

/**
 * Tek bir çökme kaydı.
 *
 * **Gizlilik:** `message` ve `stackTrace` serbest metindir ve PII taşıyabilir; sunucu ikisini de
 * kırpar ve `PiiSanitizer` ile redakte eder. `userId` gövdede YOKTUR — varsa JWT'den yazılır.
 *
 * @property occurredAt çökmenin CİHAZ saatiyle epoch ms'i; sunucu makul pencereye kırpar.
 * @property stackTrace kırpılmış yığın izi; gruplama bunun SHA-256 özetiyle yapılır.
 * @property fatal süreç ölümüyle sonuçlanan çökme mi (yakalanmış hata raporu değil).
 */
@Serializable
data class CrashReportItem(
    val occurredAt: Long,
    val exceptionType: String,
    val message: String? = null,
    val stackTrace: String,
    val platform: String,
    val appVersion: String? = null,
    val buildNumber: String? = null,
    val osVersion: String? = null,
    val deviceModel: String? = null,
    val fatal: Boolean = true,
)

/**
 * `POST /api/v1/telemetry/crash-reports` gövdesi — toplu gönderim.
 *
 * Toplu, çünkü bir cihazda üst üste birkaç çökme birikmiş olabilir ve hepsi tek açılışta,
 * tek istekte gider.
 */
@Serializable
data class CrashReportBatchRequest(
    val reports: List<CrashReportItem>,
)

/**
 * Aynı `stackTraceHash`'e sahip çökmelerin TEK satırı — ham liste kullanışsız olduğu için panel
 * yalnız grupları gösterir.
 *
 * @property affectedUsers kimliği bilinen (JWT'li) BENZERSİZ kullanıcı sayısı; misafir çökmeleri
 *   `occurrences`'a girer ama burada sayılamaz.
 * @property sampleStackTrace gruptaki EN SON kaydın yığın izi (hepsi aynı özete sahiptir).
 */
@Serializable
data class CrashGroupDto(
    val stackTraceHash: String,
    val exceptionType: String,
    val message: String? = null,
    val platforms: List<String>,
    val appVersions: List<String>,
    val occurrences: Int,
    val affectedUsers: Int,
    val firstSeenAt: Long,
    val lastSeenAt: Long,
    val sampleStackTrace: String,
)

/** Pencere içindeki TEK bir günün (TR saati) çökme sayısı. */
@Serializable
data class CrashDayDto(
    val date: String,
    val occurrences: Int,
)

/**
 * `GET /api/v1/admin/web/crashes` yanıtı.
 *
 * `groups` sayfalıdır (`occurrences` azalan); `total*` alanları pencerenin TAMAMINI kapsar,
 * sayfayı değil.
 */
@Serializable
data class CrashSummaryResponse(
    val windowDays: Int,
    val totalOccurrences: Int,
    val totalGroups: Int,
    val affectedUsers: Int,
    val days: List<CrashDayDto>,
    val groups: List<CrashGroupDto>,
    val hasMore: Boolean,
)

// ─── Satın alma denetim kaydı ──────────────────────────────────────────────────────────────

/** Admin panelindeki tek güvenli billing olayı; e-posta users tablosundan salt-okunur join'dir. */
@Serializable
data class BillingAuditEventDto(
    val id: Long,
    val eventId: String,
    val flowId: String,
    val clientId: String,
    val createdAt: Long,
    val occurredAt: Long,
    val userId: Long? = null,
    val email: String? = null,
    val source: String,
    val stage: String,
    val outcome: String,
    val platform: String,
    val productId: String? = null,
    val basePlanId: String? = null,
    val productType: String? = null,
    val errorCode: String? = null,
    val message: String? = null,
    val latencyMs: Long? = null,
    val attempt: Int? = null,
    val storeResponseCode: Int? = null,
    val subscriptionState: String? = null,
    val appVersion: String? = null,
    val buildNumber: String? = null,
    val osVersion: String? = null,
    val deviceModel: String? = null,
)

/** Filtrelenmiş billing olaylarının sayfalı admin yanıtı. */
@Serializable
data class BillingAuditEventsResponse(
    val total: Int,
    val events: List<BillingAuditEventDto>,
    val hasMore: Boolean,
)

// ─── Mobil uygulama analytics olayları ───────────────────────────────────────────────────

/**
 * Firebase'e gönderilen yapısal analytics olayının kendi backend'imize aynalanan güvenli biçimi.
 * Kullanıcı kimliği BİLEREK yoktur; ingest route'u kimliği yalnız doğrulanmış JWT'den bağlar.
 */
@Serializable
data class AppAnalyticsEventItem(
    val eventId: String,
    val sessionId: String,
    val occurredAt: Long,
    val name: String,
    val params: Map<String, String> = emptyMap(),
    val platform: String,
    val appVersion: String? = null,
    val buildNumber: String? = null,
    val osVersion: String? = null,
    val deviceModel: String? = null,
)

/** `POST /api/v1/telemetry/app-events` gövdesi. */
@Serializable
data class AppAnalyticsEventBatchRequest(
    val events: List<AppAnalyticsEventItem>,
)

/** Admin panelde seçilebilir, event üretmiş tek kullanıcı özeti. */
@Serializable
data class AdminAppEventUserDto(
    val userId: Long,
    val email: String,
    val displayName: String,
    val eventCount: Int,
    val lastOccurredAt: Long,
)

/** Event üretmiş kullanıcıların sayfalı admin yanıtı. */
@Serializable
data class AdminAppEventUsersResponse(
    val total: Int,
    val users: List<AdminAppEventUserDto>,
    val hasMore: Boolean,
)

/** Admin zaman akışındaki tek mobil analytics olayı. */
@Serializable
data class AdminAppEventDto(
    val id: Long,
    val eventId: String,
    val sessionId: String,
    val receivedAt: Long,
    val occurredAt: Long,
    val userId: Long,
    val email: String,
    val name: String,
    val params: Map<String, String> = emptyMap(),
    val platform: String,
    val appVersion: String? = null,
    val buildNumber: String? = null,
    val osVersion: String? = null,
    val deviceModel: String? = null,
)

/** Seçili kullanıcı ve filtreler için sayfalı event zaman akışı. */
@Serializable
data class AdminAppEventsResponse(
    val total: Int,
    val events: List<AdminAppEventDto>,
    val hasMore: Boolean,
)
